import { CronExpressionParser } from "cron-parser";
import dayjs, { Dayjs } from "dayjs";
import { filesize } from "filesize";
import { t } from "i18next";
import { friendlyFormatIBAN } from "ibantools";
import { isValidPhoneNumber } from "libphonenumber-js";
import { parsePhoneNumberWithError } from "libphonenumber-js/max";
import { DEFAULT_LOCALE } from "../../app/i18n";
import type { PlaceOfInsurance } from "../../modules/calculator/calcs/realty/types";
import type { CommissionsSettingsLevelBase } from "../../modules/commissions/level/types";
import type { ProductBase } from "../../modules/product/types";
import type { Address } from "../types";
import { ALL_WHITE_SPACES_PATTERN, insertToString, stripAccents } from "./utils";

export const formatIban = (iban: string) => friendlyFormatIBAN(iban) ?? "";

export const formatPhoneNumber = (phone: string) =>
  isValidPhoneNumber(phone, "SK") ? parsePhoneNumberWithError(phone, "SK").formatInternational() : phone;

export const formatLocalePhoneNumber = (phone: string) =>
  isValidPhoneNumber(phone, "SK") ? parsePhoneNumberWithError(phone, "SK").formatNational() : phone;

export const formatLocaleCurrency = (input: number, fractionDigits: number = 2): string =>
  Intl.NumberFormat(DEFAULT_LOCALE, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(input);

export const formatLocaleNumber = (input: number, fractionDigits: number = 0) =>
  Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(input);

export const formatLocaleCurrencyWithMillionLabel = (input: number, fractionDigits: number = 2) =>
  formatLocaleNumber(input, fractionDigits) + " mil €";

export const formatLocalePercentageNumber = (input: number, fractionDigits: number = 2) =>
  formatLocaleNumber(input, fractionDigits) + "\u00A0%";

export const formatLocaleNettoPoints = (input: number, fractionDigits: number = 2): string =>
  formatLocaleNumber(input, fractionDigits) + " B";

export const formatLocaleBruttoPoints = (input: number, fractionDigits: number = 2): string =>
  formatLocaleNumber(input, fractionDigits) + " BB";

export const formatLocaleDate = (input: string | Dayjs): string => dayjs(input).format("L");

export const formatLocaleDateTime = (input: string | Dayjs): string => dayjs(input).format("L LTS");

export const formatFileSize = (size: number): string => filesize(size, { locale: DEFAULT_LOCALE });

export const formatLicensePlate = (input: string) => {
  let plate = input.replace(ALL_WHITE_SPACES_PATTERN, "");
  plate = plate.length > 2 ? insertToString(plate, " ", 2) : plate;
  plate = plate.length > 6 ? insertToString(plate, " ", 6) : plate;

  return plate.toUpperCase();
};

const hasText = (value?: string): value is string => !!value && value.length > 0;

const formatHouseNumber = ({ descriptiveNumber, orientationNumber }: Address) => {
  if (hasText(descriptiveNumber)) {
    return hasText(orientationNumber) ? `${descriptiveNumber}/${orientationNumber}` : descriptiveNumber;
  }

  return hasText(orientationNumber) ? orientationNumber : "";
};

const formatParcelInfo = ({ parcelNumber1, parcelNumber2, cadastralArea }: PlaceOfInsurance) => {
  if (!hasText(parcelNumber1)) {
    return "";
  }

  const numbers = [parcelNumber1, parcelNumber2].filter(hasText).join(", ");
  const area = hasText(cadastralArea) ? `, ${t("address.attrs.cadastralAreaAbbr")} ${cadastralArea}` : "";

  return `(${t("address.attrs.parcelNumber")} ${numbers}${area})`;
};

/** Builds "<street or city> <house number> <parcel info>, <zip> <city> - <district>, <country>" */
const formatLocation = (address: Address, showCountry: boolean, parcelInfo: string = "") => {
  const streetLine = [
    hasText(address.street) ? address.street : address.city,
    ...[formatHouseNumber(address), parcelInfo].filter(hasText),
  ].join(" ");

  const cityLine =
    `${address.zipCode} ${address.city}` + (hasText(address.cityDistrict) ? ` - ${address.cityDistrict}` : "");

  return [streetLine, cityLine, ...(showCountry ? [address.country] : [])].join(", ");
};

export const formatAddress = (address: Address, showCountry: boolean = true) => formatLocation(address, showCountry);

export const formatPlaceOfInsurance = (place: PlaceOfInsurance, showCountry?: boolean) =>
  formatLocation(place, !!showCountry, formatParcelInfo(place));

export const formatAggregatedName = (entity: {
  academicDegree?: string;
  firstName?: string;
  lastName?: string;
  academicDegreeAfter?: string;
}) => {
  let aggregatedName = "";
  if (entity.academicDegree) {
    aggregatedName += `${entity.academicDegree} `;
  }

  aggregatedName += `${entity.firstName || ""} ${entity.lastName || ""}`;

  if (entity.academicDegreeAfter) {
    aggregatedName += `, ${entity.academicDegreeAfter}`;
  }

  return aggregatedName;
};

export const formatProductName = (product: ProductBase) =>
  product.deactivated ? `${product.name} (${t("product.helpers.deactivated")})` : product.name;

export const formatCoverageLimitValues = (health: number, property: number) =>
  formatLocaleCurrencyWithMillionLabel(health) + " / " + formatLocaleCurrencyWithMillionLabel(property);

export const formatCommissionsLevelName = (level: CommissionsSettingsLevelBase) => level.code + " - " + level.name;

export const formatCron = (value: string, nextDatesCount: number = 1) => {
  try {
    const expression = CronExpressionParser.parse(value, { tz: "Europe/Bratislava" });
    const results = [];
    for (let i = 0; i < nextDatesCount; i++) {
      if (expression.hasNext()) {
        const time = expression.next().toISOString();
        if (time) {
          results.push(formatLocaleDateTime(time));
        }
      }
    }
    return results.join(" | ");
  } catch {
    return "";
  }
};

export const sanitize = (str: string) => stripAccents(str).toLowerCase().trim();

export const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

export const formatDateRange = (min: string, max: string) => `${formatLocaleDate(min)} ~ ${formatLocaleDate(max)}`;

const EVERY_FOURTH_CHAR_PATTERN = new RegExp(/(.{4})/, "g");
const EVERY_THIRD_CHAR_PATTERN = new RegExp(/(.{3})/, "g");
const BEGINNING_END_WHITE_SPACES_PATTER = new RegExp(/(^\s+|\s+$)/, "g");

export const formatThreeSpacedString = (input: string) =>
  input
    .replace(ALL_WHITE_SPACES_PATTERN, "")
    .replace(EVERY_THIRD_CHAR_PATTERN, "$1 ")
    .replace(BEGINNING_END_WHITE_SPACES_PATTER, "");
export const formatFourSpacedString = (input: string) =>
  input
    .replace(ALL_WHITE_SPACES_PATTERN, "")
    .replace(EVERY_FOURTH_CHAR_PATTERN, "$1 ")
    .replace(BEGINNING_END_WHITE_SPACES_PATTER, "");
